import "./Forms.scss";

import {
    EuiButton,
    EuiCallOut,
    EuiFlexGroup,
    EuiFlexItem,
    EuiForm,
    EuiFormRow,
    EuiSelect,
    EuiSpacer,
    EuiSwitch,
    EuiText,
} from "@elastic/eui";
import React, { useContext, useEffect, useState } from "react";
import { JsonPrice } from "utils/types";

import client from "../api/Client";
import API_URL from "../Constants";
import { generateInternalProductId } from "../utils/FormUtils";
import { useCurrentShopGroup } from "../utils/PricelistUtils";
import { ShopContext } from "../utils/Shop";
import { PriceCombiForm } from "./PriceCombiForm";

function CannabisFormNew({
    product,
    preselectedWeed,
}: {
    product: object | undefined;
    preselectedWeed: string | undefined;
}) {
    const shopContext = useContext(ShopContext);
    const shopGroupId = useCurrentShopGroup().id;
    const [redirectToListEnabled, setRedirectToListEnabled] = useState(true);
    const [prices, setPrices] = useState([]);
    const [priceOptions, setPriceOptions] = useState([]);
    const [pricesLoaded, setPricesLoaded] = useState(false);
    const [categories, setCategories] = useState([]);
    const [categoriesLoaded, setCategoriesLoaded] = useState(false);
    const [kinds, setKinds] = useState([]);
    const [kindsLoaded, setKindsLoaded] = useState(false);
    const [selectedPrice, setSelectedPrice] = useState(product ? product.price_id : "-1");
    const [selectedCategory, setSelectedCategory] = useState(product ? product.category_id : "-1");
    const [selectedKind, setSelectedKind] = useState(product ? product.kind_id : "-1");
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(undefined);
    const [messageHelp, setMessageHelp] = useState(undefined);
    const [errors, setErrors] = useState(false);

    const [checkedHalf] = useState(false);
    const [checkedOne] = useState(false);
    const [checkedTwoHalf] = useState(false);
    const [checkedFive] = useState(false);
    const [checkedJoint] = useState(false);
    const [checkedPiece] = useState(false);
    const [checkedNew, setCheckedNew] = useState(false);

    const [useExistingPrice, setUseExistingPrice] = useState(product ? true : false);
    const [existingPrice, setExistingPrice] = useState(undefined);
    const [edibleJsonPrices, setEdibleJsonPrices] = useState<JsonPrice[]>([]);
    const [preRolledJsonPrices, setPreRolledJsonPrices] = useState<JsonPrice[]>([]);
    const [cannabisJsonPrices, setCannabisJsonPrices] = useState<JsonPrice[]>([]);

    const [internalIdPrefix, setInternalIdPrefix] = useState<string>("000");

    const currentShopGroup = useCurrentShopGroup();
    useEffect(() => {
        setCheckedNew(
            !localStorage.getItem("markPriceAsNew")
                ? true
                : localStorage.getItem("markPriceAsNew") === "true"
                ? true
                : false
        );

        setRedirectToListEnabled(
            !localStorage.getItem("redirectToPriceList")
                ? false
                : localStorage.getItem("redirectToPriceList") === "true"
                ? true
                : false
        );

        client.get(`${API_URL}/v1/prices?skip=0&limit=1000&sort=internal_product_id`).then((result) => {
            // prepare stuff for EuiSuperSelect
            const priceOptions = result.data
                .filter((price) => price.shop_group_id === currentShopGroup.id)
                .filter(
                    (price) =>
                        price.pre_rolled_joints?.length > 0 || price.cannabis?.length > 0 || price.edible?.length > 0
                )
                .map((price) => ({
                    text: price.internal_product_id,
                    value: price.id,
                }));
            priceOptions.unshift({ text: "", value: "-1" });
            setPrices(result.data.filter((price) => price.shop_group_id === currentShopGroup.id));
            setPriceOptions(priceOptions);
            setPricesLoaded(true);
            setSelectedPrice("-1");

            if (product) {
                setSelectedPrice(product.price_id);
                setCheckedNew(product.new);
                client.get(`${API_URL}/v1/prices/${product.price_id}`).then((res) => {
                    setCannabisJsonPrices(res.data.cannabis ?? []);
                    setPreRolledJsonPrices(res.data.pre_rolled_joints ?? []);
                    setEdibleJsonPrices(res.data.edible ?? []);
                    setInternalIdPrefix(res.data.internal_product_id.substring(0, 3));
                });
            }
        });

        client.get(`${API_URL}/v1/categories?skip=0&limit=1000&sort=name`).then((result) => {
            const filterCategories = result.data.filter(
                (value) => value.shop_id === shopContext.shopId && value.cannabis
            );
            let categories = filterCategories.map((category) => {
                category.text = category.name;
                category.value = category.id;
                return category;
            });
            // Add blanco first option
            categories.unshift({ text: "", value: "-1" });

            setCategories(categories);
            setCategoriesLoaded(true);
            setSelectedCategory(product ? product.category_id : "-1");
        });
        client.get(`${API_URL}/v1/kinds/all`).then((result) => {
            const sortedData = result.data
                .filter((kind) => kind.shop_group_id === currentShopGroup.id || kind.shop_group_id === null)
                .sort((a, b) => a.name.localeCompare(b.name));
            const kinds = sortedData.map((kind) => {
                kind.text = kind.shop_group_id ? `${kind.name}` : `${kind.name}  (Global)`;
                kind.value = kind.id;
                return kind;
            });
            // Add blanco first option
            kinds.unshift({ text: "", value: "-1" });
            let selectedKind = product ? product["kind_id" as keyof object] : "-1";
            if (preselectedWeed) {
                // console.log("Preselecting a wiet", preselectedWeed);
                selectedKind = preselectedWeed;
            }
            setKinds(kinds);
            setKindsLoaded(true);
            setSelectedKind(selectedKind);
        });
    }, [preselectedWeed, product, shopContext.shopId, currentShopGroup.id]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (selectedKind === "-1" || selectedCategory === "-1") {
            setMessage("Error");
            setMessageHelp(
                "Het form is niet compleet ingevuld. Prijs, categorie en product-soort moeten ingevuld zijn."
            );
            setErrors(true);
            setTimeout(() => {
                setMessage(undefined);
                setMessageHelp(undefined);
                setErrors(false);
            }, 3000);
            return;
        }

        let payload = {
            shop_id: shopContext.shopId,
            new: checkedNew,
            price_id: selectedPrice,
            product_id: null,
            category_id: selectedCategory,
            kind_id: selectedKind,
            use_half: checkedHalf,
            use_one: checkedOne,
            use_two_five: checkedTwoHalf,
            use_five: checkedFive,
            use_joint: checkedJoint,
            use_piece: checkedPiece,
        };
        if (!product) {
            payload["active"] = true;
        } else {
            payload["active"] = product["active" as keyof object];
        }

        setLoading(true);
        const kindName = kinds.find((kind) => kind.id === selectedKind)!.name;

        const internalProductId = generateInternalProductId(internalIdPrefix, [
            cannabisJsonPrices ?? [],
            preRolledJsonPrices ?? [],
            edibleJsonPrices ?? [],
        ]);

        if (product) {
            if (
                !useExistingPrice ||
                (useExistingPrice &&
                    existingPrice &&
                    existingPrice.internal_product_id !== internalProductId.substring(0, internalProductId.length - 3))
            ) {
                const pricePayload = {
                    edible: edibleJsonPrices,
                    pre_rolled_joints: preRolledJsonPrices,
                    cannabis: cannabisJsonPrices,
                    internal_product_id: internalProductId.substring(0, internalProductId.length - 3),
                    shop_group_id: currentShopGroup.id,
                };

                await client.post(`/v1/prices`, pricePayload).then((priceResult) => {
                    payload.price_id = priceResult.data.id;
                });
            }
            client
                .put(`/v1/shops-to-prices/${product.id}`, payload) // Todo: probably jsonify needed
                .then(() => {
                    setMessage(`Product ${kindName} is opgeslagen. Redirecting...`);
                    setMessageHelp(undefined);
                    setErrors(false);
                    setLoading(false);
                    setTimeout(() => {
                        window.location.replace("/products/cannabis");
                    }, 3000);
                })
                .catch((result) => {
                    console.log(result);
                    setLoading(false);
                    if (result.status_code === 409) {
                        setMessage("Error");
                        setMessageHelp("U heeft dit product al toegevoegd aan deze shop!");
                        setErrors(true);
                        setTimeout(() => {
                            setMessage(undefined);
                            setMessageHelp(undefined);
                            setErrors(false);
                        }, 3000);
                    } else {
                        setMessage("Error");
                        setMessageHelp("Een form veld klopt niet.");
                        setErrors(true);
                        setTimeout(() => {
                            setMessage(undefined);
                            setMessageHelp(undefined);
                            setErrors(false);
                        }, 3000);
                    }
                });
        } else {
            if (
                !useExistingPrice ||
                (useExistingPrice &&
                    existingPrice &&
                    existingPrice.internal_product_id !== internalProductId.substring(0, internalProductId.length - 3))
            ) {
                const pricePayload = {
                    edible: edibleJsonPrices,
                    pre_rolled_joints: preRolledJsonPrices,
                    cannabis: cannabisJsonPrices,
                    internal_product_id: internalProductId.substring(0, internalProductId.length - 3),
                    shop_group_id: shopGroupId,
                };

                await client.post(`/v1/prices`, pricePayload).then((priceResult) => {
                    payload.price_id = priceResult.data.id;
                });
            }

            client
                .post(`/v1/shops-to-prices`, payload)
                .then(() => {
                    setMessage(`Product ${kindName} is toegevoegd.`);
                    setMessageHelp(undefined);
                    setErrors(false);
                    setLoading(false);
                    if (!redirectToListEnabled) {
                        setTimeout(() => {
                            window.location.replace("/products/cannabis");
                        }, 3000);
                    } else {
                        resetFormState();
                        setTimeout(() => {
                            setMessage(undefined);
                            setMessageHelp(undefined);
                            setErrors(false);
                        }, 3000);
                    }
                })
                .catch((result) => {
                    setLoading(false);
                    if (result.message === "Request failed with status code 409") {
                        setMessage("Error");
                        setMessageHelp("Deze prijsvariatie bestaat al.");
                        setErrors(true);
                        setTimeout(() => {
                            setMessage(undefined);
                            setMessageHelp(undefined);
                            setErrors(false);
                        }, 3000);
                    } else {
                        setMessage("Error");
                        setMessageHelp("Een form veld klopt niet.");
                        setErrors(true);
                        setTimeout(() => {
                            setMessage(undefined);
                            setMessageHelp(undefined);
                            setErrors(false);
                        }, 3000);
                    }
                });
        }
    };

    const resetFormState = () => {
        setSelectedKind("-1");
        setSelectedPrice("-1");
        setSelectedCategory("-1");
        setEdibleJsonPrices([]);
        setPreRolledJsonPrices([]);
        setCannabisJsonPrices([]);
    };

    const handleKeyDown = (event) => {
        if (event.keyCode === 13) {
            console.log("Enter key pressed; submitting form");
            handleSubmit(event);
        }
    };

    function setSelectedBasePrice(priceId) {
        setSelectedPrice(priceId);
        if (priceId === "-1") {
            return;
        }
        const price = prices.find((p) => p.id === priceId)!;
        setExistingPrice(price);
        setInternalIdPrefix(price.internal_product_id.substring(0, 3));
        setCannabisJsonPrices(price.cannabis);
        setPreRolledJsonPrices(price.pre_rolled_joints);
        setEdibleJsonPrices(price.edible);
    }

    return (
        <EuiForm>
            {message && (
                <>
                    <EuiCallOut
                        title={message}
                        color={errors ? "danger" : "primary"}
                        iconType={errors ? "alert" : "notebookApp"}
                    >
                        {messageHelp && <p>{messageHelp}</p>}
                    </EuiCallOut>
                    <EuiSpacer />
                </>
            )}

            {!product && (
                <>
                    <EuiSwitch
                        label="Blijf op deze pagina na toevoegen"
                        checked={redirectToListEnabled}
                        onChange={(e) => {
                            setRedirectToListEnabled(e.target.checked);
                            localStorage.setItem("redirectToPriceList", e.target.checked.toString());
                        }}
                    />
                    <EuiSpacer />
                </>
            )}

            <EuiSwitch
                label="Publiceer product met label 'NEW'"
                checked={checkedNew}
                onChange={(e) => {
                    setCheckedNew(e.target.checked);
                    localStorage.setItem("markPriceAsNew", e.target.checked.toString());
                }}
            />
            <EuiSpacer />

            <EuiFormRow
                label="Categorie"
                labelAppend={<EuiText size="m">Selecteer een categorie</EuiText>}
                id="select-category"
                fullWidth
            >
                <EuiSelect
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    options={categories}
                    isLoading={!categoriesLoaded}
                    autoFocus={true}
                    onKeyDown={handleKeyDown}
                    fullWidth
                />
            </EuiFormRow>

            <EuiSpacer />

            <EuiFormRow
                label="Soort"
                labelAppend={
                    <EuiText size="m">Selecteer een cannabis soort. (Global zijn beschikbaar voor alle shops.)</EuiText>
                }
                id="select-kind"
                fullWidth
            >
                <EuiSelect
                    value={selectedKind}
                    onChange={(e) => setSelectedKind(e.target.value)}
                    options={kinds}
                    isLoading={!kindsLoaded}
                    onKeyDown={handleKeyDown}
                    fullWidth
                />
            </EuiFormRow>

            {!product && (
                <EuiSwitch
                    label="Bestaande prijs combi als basis gebruiken?"
                    checked={!!useExistingPrice}
                    onChange={(e) => setUseExistingPrice(e.target.checked)}
                />
            )}

            <EuiSpacer />

            {useExistingPrice && (
                <EuiFormRow
                    label="Prijs combi"
                    labelAppend={<EuiText size="m">Selecteer een prijs combi</EuiText>}
                    id="select-price"
                    fullWidth
                >
                    <EuiSelect
                        value={selectedPrice}
                        onChange={(e) => setSelectedBasePrice(e.target.value)}
                        options={priceOptions}
                        isLoading={!pricesLoaded}
                        autoFocus={true}
                        onKeyDown={handleKeyDown}
                        fullWidth
                    />
                </EuiFormRow>
            )}

            <PriceCombiForm
                useExistingPrice={useExistingPrice}
                edibleJsonPrices={edibleJsonPrices}
                setEdibleJsonPrices={setEdibleJsonPrices}
                preRolledJsonPrices={preRolledJsonPrices}
                setPreRolledJsonPrices={setPreRolledJsonPrices}
                cannabisJsonPrices={cannabisJsonPrices}
                setCannabisJsonPrices={setCannabisJsonPrices}
                setInternalIdPrefix={setInternalIdPrefix}
                internalIdPrefix={internalIdPrefix}
            />

            <EuiSpacer />

            <EuiFormRow>
                <EuiFlexGroup>
                    {!product && (
                        <EuiFlexItem>
                            {" "}
                            <EuiButton fill color="danger" onClick={resetFormState} disabled={false}>
                                Reset
                            </EuiButton>
                        </EuiFlexItem>
                    )}

                    <EuiFlexItem>
                        {useExistingPrice ? (
                            <EuiButton
                                fill
                                onClick={(e) => handleSubmit(e)}
                                disabled={selectedKind === "-1" || selectedCategory === "-1" || selectedPrice === "-1"}
                                type="submit"
                                isLoading={loading}
                            >
                                {product ? "Opslaan" : "Toevoegen"}
                            </EuiButton>
                        ) : (
                            <EuiButton
                                fill
                                onClick={(e) => handleSubmit(e)}
                                disabled={
                                    selectedKind === "-1" ||
                                    selectedCategory === "-1" ||
                                    (edibleJsonPrices.length < 1 &&
                                        preRolledJsonPrices.length < 1 &&
                                        cannabisJsonPrices.length < 1)
                                }
                                type="submit"
                                isLoading={loading}
                            >
                                {product ? "Opslaan" : "Toevoegen"}
                            </EuiButton>
                        )}
                    </EuiFlexItem>
                </EuiFlexGroup>
            </EuiFormRow>
            {message && (
                <>
                    <EuiCallOut
                        title={message}
                        color={errors ? "danger" : "primary"}
                        iconType={errors ? "alert" : "notebookApp"}
                    >
                        {messageHelp && <p>{messageHelp}</p>}
                    </EuiCallOut>
                    <EuiSpacer />
                </>
            )}
        </EuiForm>
    );
}

export default CannabisFormNew;
